import { Money, convertMoneyToFloat, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { formattedNumberCompact } from '@betterment/shared-components/src/utils/accountSecurityHelpers';

import {
  DirectTradingAccountSecurityFundamentalsDocument,
  DirectTradingAccountSecurityFundamentalsQuery,
} from './graphql/directTradingAccountSecurityFundamentals.generated';

import { NarrowTypename } from '../../../graphql/utils/narrow-typename';
import { ExpandableTextBox } from '../../../Shared/components-v2/expandable-text-box/ExpandableTextBox';

type FinancialSecurity = NarrowTypename<
  DirectTradingAccountSecurityFundamentalsQuery['account'],
  'DirectTradingAccount'
>['financialSecurity'];

const UnavailableText: React.FC = () => {
  return (
    <Text role="cell" as="p" variant="text50">
      Unavailable
    </Text>
  );
};

type FundProps = {
  financialSecurity: NarrowTypename<FinancialSecurity, 'FinancialSecurityFund'>;
};

const FundDetails: React.FC<FundProps> = ({ financialSecurity }) => {
  const { fundamentals } = financialSecurity;

  if (fundamentals.__typename !== 'FinancialSecurityFundamentalsFund') {
    return null;
  }

  return (
    <Card.Content
      css={{
        gap: 'space.300',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Box css={{ width: '100%' }}>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr 1fr 1fr 1fr',
              base: '1fr 1fr',
            },
            rowGap: 'space.200',
            alignItems: 'start',
          }}
          role="table"
          aria-label="fundamentals"
        >
          <Box role="row" aria-label="market cap">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Market cap
            </Text>
            {fundamentals.marketCap ? (
              <Text role="cell" as="p" variant="text50">
                ${formattedNumberCompact(convertMoneyToFloat(fundamentals.marketCap.value))}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box role="row" aria-label="expense ratio">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Expense ratio
            </Text>
            {fundamentals.expenseRatio ? (
              <Text role="cell" as="p" variant="text50">
                {fundamentals.expenseRatio.value}%
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box role="row" aria-label="dividend yield">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Div yield (TTM)
            </Text>
            {fundamentals.trailingTwelveMonths.dividendYield ? (
              <Text role="cell" as="p" variant="text50">
                {fundamentals.trailingTwelveMonths.dividendYield.value}%
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
        </Box>
        {fundamentals.description && (
          <>
            <Separator css={{ marginY: 'space.200' }} />
            <ExpandableTextBox>
              <Text variant="text50" dangerouslySetInnerHTML={{ __html: fundamentals.description }} />
            </ExpandableTextBox>
          </>
        )}
        <Separator css={{ marginY: 'space.200' }} />
        <Box>
          <SambaAutoLink to={financialSecurity.prospectusUrl} target="_blank">
            Prospectus
          </SambaAutoLink>
        </Box>
      </Box>
    </Card.Content>
  );
};

type StockProps = {
  financialSecurity: NarrowTypename<FinancialSecurity, 'FinancialSecurityStock'>;
};

const StockDetails: React.FC<StockProps> = ({ financialSecurity }) => {
  const { fundamentals } = financialSecurity;

  if (fundamentals.__typename !== 'FinancialSecurityFundamentalsStock') {
    return null;
  }

  return (
    <Card.Content
      css={{
        gap: 'space.300',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Box css={{ width: '100%' }}>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr 1fr 1fr 1fr',
              base: '1fr 1fr',
            },
            rowGap: 'space.200',
            alignItems: 'start',
          }}
          role="table"
          aria-label="fundamentals"
        >
          <Box role="row" aria-label="market cap">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Market cap
            </Text>
            {fundamentals.marketCap ? (
              <Text role="cell" as="p" variant="text50">
                ${formattedNumberCompact(convertMoneyToFloat(fundamentals.marketCap.value))}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box role="row" aria-label="earnings per share">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              EPS (TTM)
            </Text>
            {fundamentals.trailingTwelveMonths.earningsPerShare ? (
              <Text role="cell" as="p" variant="text50">
                {formatMoney(Money(fundamentals.trailingTwelveMonths.earningsPerShare.value.cents), 'default')}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box role="row" aria-label="dividend yield">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Div yield (TTM)
            </Text>
            {fundamentals.trailingTwelveMonths.dividendYield ? (
              <Text role="cell" as="p" variant="text50">
                {fundamentals.trailingTwelveMonths.dividendYield.value}%
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box role="row" aria-label="shares outstanding">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Shares outstanding
            </Text>
            {fundamentals.sharesOutstanding ? (
              <Text role="cell" as="p" variant="text50">
                {formattedNumberCompact(fundamentals.sharesOutstanding.value)}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box css={{ gridColumn: { base: '1 / span 2', lg: '1' } }} role="row" aria-label="pe ratio">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              P/E ratio
            </Text>
            {fundamentals.priceToEarningsRatio ? (
              <Text role="cell" as="p" variant="text50">
                {fundamentals.priceToEarningsRatio.value}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
        </Box>
        <Separator css={{ marginY: 'space.200' }} />
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr 1fr 1fr 1fr',
              base: '1fr 1fr',
            },
            rowGap: 'space.200',
            alignItems: 'start',
          }}
          role="table"
          aria-label="fundamentals"
        >
          <Box role="row" aria-label="revenue">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              Revenue (TTM)
            </Text>
            {fundamentals.trailingTwelveMonths.revenue ? (
              <Text role="cell" as="p" variant="text50">
                ${formattedNumberCompact(convertMoneyToFloat(fundamentals.trailingTwelveMonths.revenue.value))}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
          <Box role="row" aria-label="ebitda">
            <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
              EBITDA (TTM)
            </Text>
            {fundamentals.trailingTwelveMonths.ebitda ? (
              <Text role="cell" as="p" variant="text50">
                ${formattedNumberCompact(convertMoneyToFloat(fundamentals.trailingTwelveMonths.ebitda.value))}
              </Text>
            ) : (
              <UnavailableText />
            )}
          </Box>
        </Box>
        {fundamentals.description && (
          <>
            <Separator css={{ marginY: 'space.200' }} />
            <ExpandableTextBox>
              <Text variant="text50" dangerouslySetInnerHTML={{ __html: fundamentals.description }} />
            </ExpandableTextBox>
          </>
        )}
      </Box>
    </Card.Content>
  );
};

type Props = {
  accountId: string;
  securityId: string;
};

export const FundamentalsCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityFundamentalsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  if (!data.account || data.account.__typename !== 'DirectTradingAccount') {
    return null;
  }

  const { account } = data;
  const { financialSecurity } = account;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <H2 variant="title200">About</H2>
      </Card.Header>
      <Separator />
      {financialSecurity.__typename === 'FinancialSecurityFund' && (
        <FundDetails financialSecurity={financialSecurity} />
      )}
      {financialSecurity.__typename === 'FinancialSecurityStock' && (
        <StockDetails financialSecurity={financialSecurity} />
      )}
    </Card>
  );
};
